import { Loading } from '@arcflight/tf-component-library';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Title } from '../../pages/AircraftMaintenance/MXItemDrawer';
import AuthDropdownMenu from '../AuthDropdownMenu/AuthDropdownMenu';
import ContentTabs from '../ContentTabs/ContentTabs';
import MaintenanceDrawer from '../MaintenanceDrawer/MaintenanceDrawer';
import { DashboardState } from '../../models';
import { AircraftResource } from '../../models/aircraft';
import { fetchAircraftStatus } from '../../models/aircraft/actions';
import { changeDrawerMode, changeDrawerVisibility } from '../../models/drawer';
import { Maintenance, MaintenanceActionTypes } from '../../models/maintenance';
import { ToastCategories, ToastTypes, addToast } from '../../models/toasts';
import { removeMaintenance } from '../../services/api';
import { getSingleMxItem } from '../../services/apiNew';
import MaintenanceItemHistoryTab from './MaintenanceItemHistoryTab';

export function getAreaLabelFromScheduledMxType(mxType: string): string {
  if (mxType.startsWith('engine')) {
    return `${mxType.split('_')[0]} ${mxType.split('_')[1]}`;
  }
  return mxType.split('_')[0];
}

const MxDrawerTabsShell = styled.div`
  width: 100%;
  box-sizing: border-box;

  & > div {
    margin-top: 0;
  }

  .ant-tabs-bar {
    margin: 0 48px 0 48px;
  }

  @media (max-width: 450px) {
    .ant-tabs-bar {
      margin: 0 24px 0 24px;
    }
  }
`;

const HeaderStrip = styled.div`
  padding: 48px 48px 0 48px;

  @media (max-width: 450px) {
    padding: 48px 24px 0 24px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  width: 100%;
  margin-bottom: 12px;
`;

const HeaderTitleSlot = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
`;

const HeaderActions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ItemTitleHeading = styled(Title)`
  margin-bottom: 0;
`;

export const MX_MAINTENANCE_ITEM_TAB_KEYS = {
  details: 'details',
  history: 'history',
} as const;

export type MXMaintenanceItemInitialTab =
  (typeof MX_MAINTENANCE_ITEM_TAB_KEYS)[keyof typeof MX_MAINTENANCE_ITEM_TAB_KEYS];

type MaintenanceItemDrawerTabsProps = {
  initialTab?: MXMaintenanceItemInitialTab;
};

const MaintenanceItemDrawerTabs: React.FC<MaintenanceItemDrawerTabsProps> = ({
  initialTab = MX_MAINTENANCE_ITEM_TAB_KEYS.details,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { id: aircraftId } = useParams<{ id: string }>();

  const drawerId = useSelector((state: DashboardState) => state.drawer.drawerId);
  const drawerMode = useSelector((state: DashboardState) => state.drawer.mode);
  const maintenanceMap = useSelector((state: DashboardState) => state.maintenance.maintenanceMap);
  const itemFromMap = drawerId ? maintenanceMap.get(drawerId) : undefined;

  const [fetchedItem, setFetchedItem] = useState<Maintenance | null>(null);
  const [fetchingHeaderItem, setFetchingHeaderItem] = useState(false);

  useEffect(() => {
    setFetchedItem(null);
  }, [drawerId]);

  useEffect(() => {
    let cancelled = false;

    if (!drawerId) {
      setFetchingHeaderItem(false);
    } else if (itemFromMap) {
      setFetchingHeaderItem(false);
    } else {
      setFetchingHeaderItem(true);

      getSingleMxItem({ id: drawerId })
        .then((res) => {
          if (!cancelled) {
            setFetchedItem((res?.data as Maintenance) ?? null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFetchedItem(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setFetchingHeaderItem(false);
          }
        });
    }

    return (): void => {
      cancelled = true;
    };
  }, [drawerId, itemFromMap]);

  const headerItem = itemFromMap ?? fetchedItem;

  const handleDeleteMaintenanceItem = useCallback(async (): Promise<unknown> => {
    if (!headerItem?.id || !aircraftId) {
      return null;
    }
    try {
      const payload = {
        id: headerItem.id,
        aircraft_id: aircraftId,
      };
      const response = await removeMaintenance(payload);
      if (response && response.statusCode === 204) {
        dispatch(
          addToast({
            payload: {
              title: formatMessage({ id: 'message.itemDeleted' }),
              message: '',
              type: ToastTypes.SUCCESS,
              category: ToastCategories.FLASH,
            },
          }),
        );
        dispatch({
          type: MaintenanceActionTypes.DELETE,
          payload: { id: headerItem.id },
        });
        dispatch(fetchAircraftStatus({ payload: aircraftId }));
        dispatch(changeDrawerVisibility({ payload: false }));
      }
    } catch (error) {
      return error;
    }
    return null;
  }, [aircraftId, dispatch, formatMessage, headerItem?.id]);

  const areaLabel = useMemo(
    () => (headerItem?.mx_type ? getAreaLabelFromScheduledMxType(headerItem.mx_type) : ''),
    [headerItem?.mx_type],
  );

  const headerLoading = fetchingHeaderItem;

  let headerContent: React.ReactNode = null;
  if (headerLoading) {
    headerContent = <Loading loading />;
  } else if (headerItem) {
    headerContent = (
      <ItemTitleHeading data-testid="MaintenanceItemDrawerTabs-itemTitle">
        {areaLabel}:<span>{headerItem.name}</span>
      </ItemTitleHeading>
    );
  }

  const showHeaderActions =
    drawerMode === 'view' && !headerLoading && Boolean(headerItem?.id && aircraftId);

  const isEditing = drawerMode === 'edit' || drawerMode === 'add';

  if (isEditing) {
    return (
      <MxDrawerTabsShell>
        <MaintenanceDrawer embedActionsInParent />
      </MxDrawerTabsShell>
    );
  }

  return (
    <MxDrawerTabsShell>
      <HeaderStrip>
        <HeaderRow>
          <HeaderTitleSlot>{headerContent}</HeaderTitleSlot>
          {showHeaderActions ? (
            <HeaderActions data-testid="MaintenanceItemDrawerTabs-actions">
              <AuthDropdownMenu
                options={{ read: false, update: true, delete: true }}
                aircraftId={aircraftId}
                resource={AircraftResource.SCHEDULED_MX_ITEM}
                editCallback={(): void => {
                  dispatch(changeDrawerMode({ payload: 'edit' }));
                }}
                handleDelete={handleDeleteMaintenanceItem}
              />
            </HeaderActions>
          ) : null}
        </HeaderRow>
      </HeaderStrip>
      <ContentTabs
        tabsTestId="MaintenanceItemDrawerTabs-tabs"
        initialActiveKey={initialTab}
        tabs={[
          {
            key: MX_MAINTENANCE_ITEM_TAB_KEYS.details,
            header: formatMessage({ id: 'title.overview' }),
            tabPanelTestId: 'MaintenanceItemDrawerTabs-tab-details',
            content: <MaintenanceDrawer embedActionsInParent />,
          },
          {
            key: MX_MAINTENANCE_ITEM_TAB_KEYS.history,
            header: formatMessage({ id: 'title.auditLog' }),
            tabPanelTestId: 'MaintenanceItemDrawerTabs-tab-history',
            content: <MaintenanceItemHistoryTab />,
          },
        ]}
      />
    </MxDrawerTabsShell>
  );
};

MaintenanceItemDrawerTabs.displayName = 'MaintenanceItemDrawerTabs';

export default MaintenanceItemDrawerTabs;
